/**
 * WebUSB bridge for the Tecor TR-422B 60×50 mm thermal label printer.
 *
 * The printer speaks TSPL. We render the TSPL byte stream server-side
 * (single source of truth in backend/tspl_printer.py), fetch it as
 * base64, decode to a Uint8Array here, and push it straight to the
 * printer's bulk OUT endpoint via `navigator.usb` — no network printer,
 * no Kitchen Bridge, no popups.
 *
 * WebUSB requires a **user gesture** to pair a device the first time
 * (`requestDevice`). After pairing, the browser remembers the grant, so
 * `getDevices()` can silently re-attach on later visits.
 */
import { useCallback, useEffect, useState } from "react";

export const TECOR_VENDOR_ID = 0xfee5;
export const TECOR_PRODUCT_ID = 0x0001;
export const TECOR_FILTERS = [{ vendorId: TECOR_VENDOR_ID, productId: TECOR_PRODUCT_ID }];

let _device = null;

export function isWebUsbSupported() {
  return typeof navigator !== "undefined" && !!navigator.usb;
}

export function b64ToBytes(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/** Prompt the pairing dialog (must be called from a user gesture).
 *
 * We pass an EMPTY filter list so Chrome shows **every** connected USB
 * device in the chooser. Rebadged thermal printers (the Tecor TR-422B
 * included) frequently report vendor/product IDs that differ from the
 * documented 0xfee5/0x0001, so a narrow filter leaves the chooser empty
 * ("No compatible devices found"). Letting the user pick their printer
 * directly is the robust approach for generic TSPL hardware — we then
 * remember whatever they select.
 */
export async function requestTecorDevice() {
  if (!isWebUsbSupported()) {
    throw new Error("WebUSB isn't supported in this browser. Use Chrome or Edge on desktop/Android.");
  }
  try {
    const device = await navigator.usb.requestDevice({ filters: [] });
    _device = device;
    return device;
  } catch (e) {
    // Map raw DOMException names to friendly, actionable copy.
    if (e && e.name === "NotFoundError") {
      throw new Error("No printer selected. Plug the Tecor in, then pick it from the list.");
    }
    if (e && e.name === "SecurityError") {
      throw new Error("Printer access blocked. WebUSB needs a secure (https) connection.");
    }
    throw new Error(e?.message || "Couldn't open the printer picker.");
  }
}

/** Re-attach a previously-paired printer without a prompt. Prefers the
 * documented Tecor IDs but falls back to whatever device the user
 * previously granted (they may have picked a rebadged unit). */
export async function getPairedTecor() {
  if (!isWebUsbSupported()) return null;
  const devices = await navigator.usb.getDevices();
  const found =
    devices.find((d) => d.vendorId === TECOR_VENDOR_ID && d.productId === TECOR_PRODUCT_ID) ||
    devices[0] ||
    null;
  if (found) _device = found;
  return found;
}

async function openAndClaim(device) {
  // On Windows, device.open() itself throws "Access denied" (a
  // SecurityError/NetworkError DOMException) when the printer is bound to
  // the OS print/usbprint driver rather than WinUSB. Treat any open/
  // select/claim failure as CLAIM_FAILED so the UI offers the Zadig guide
  // instead of a cryptic "access denied".
  try {
    if (!device.opened) await device.open();
    if (device.configuration === null) await device.selectConfiguration(1);
  } catch (e) {
    const err = new Error(
      "Windows is blocking direct access to the printer (\u201caccess denied\u201d). Its driver needs switching to WinUSB \u2014 see the driver help.",
    );
    err.code = "CLAIM_FAILED";
    err.cause = e;
    throw err;
  }
  // Pick the interface that exposes a bulk OUT endpoint.
  let iface = device.configuration.interfaces.find((i) =>
    i.alternate.endpoints.some((e) => e.direction === "out" && e.type === "bulk"),
  );
  if (!iface) iface = device.configuration.interfaces[0];
  try {
    await device.claimInterface(iface.interfaceNumber);
  } catch (e) {
    // Windows binds a kernel/usbprint driver to many label printers, which
    // blocks WebUSB from claiming the interface. Flag this specifically so
    // the UI can offer the WinUSB/Zadig walkthrough.
    const err = new Error(
      "Couldn't claim the printer interface. On Windows the printer may be using the system print driver \u2014 see the driver help.",
    );
    err.code = "CLAIM_FAILED";
    err.cause = e;
    throw err;
  }
  const outEp = iface.alternate.endpoints.find((e) => e.direction === "out" && e.type === "bulk");
  if (!outEp) throw new Error("No bulk OUT endpoint on the printer.");
  return outEp.endpointNumber;
}

/** Ensure the printer can be opened (the Windows "access denied" gate)
 * without sending data — a lightweight health ping. Throws NO_DEVICE
 * when unpaired, CLAIM_FAILED when the OS is holding the device. */
export async function probePrinter() {
  const device = _device || (await getPairedTecor());
  if (!device) {
    const err = new Error("No printer paired.");
    err.code = "NO_DEVICE";
    throw err;
  }
  try {
    if (!device.opened) await device.open();
    if (device.configuration === null) await device.selectConfiguration(1);
  } catch (e) {
    const err = new Error(
      "Windows is blocking direct access to the printer (\u201caccess denied\u201d). Its driver needs switching to WinUSB \u2014 see the driver help.",
    );
    err.code = "CLAIM_FAILED";
    err.cause = e;
    throw err;
  }
  return { ok: true, name: device.productName || "Tecor TR-422B" };
}

/** Send a Uint8Array (raw TSPL) to the paired Tecor, chunked. */
export async function printBytes(bytes) {
  const device = _device || (await getPairedTecor());
  if (!device) {
    const err = new Error("No Tecor printer connected. Click \u201cConnect printer\u201d first.");
    err.code = "NO_DEVICE";
    throw err;
  }
  const endpointNumber = await openAndClaim(device);
  const CHUNK = 4096; // proxy-safe chunking for large label buffers
  for (let i = 0; i < bytes.length; i += CHUNK) {
    // eslint-disable-next-line no-await-in-loop
    await device.transferOut(endpointNumber, bytes.slice(i, i + CHUNK));
  }
  return { ok: true, sent: bytes.length };
}

/**
 * React hook — connection state + connect/print actions for the Tecor.
 */
export function useTecorPrinter() {
  const supported = isWebUsbSupported();
  const [connected, setConnected] = useState(false);
  const [deviceName, setDeviceName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    getPairedTecor()
      .then((d) => {
        if (d) {
          setConnected(true);
          setDeviceName(d.productName || "Tecor TR-422B");
        }
      })
      .catch(() => {});
    const onDisconnect = () => {
      setConnected(false);
      setDeviceName("");
      _device = null;
    };
    navigator.usb.addEventListener("disconnect", onDisconnect);
    return () => navigator.usb.removeEventListener("disconnect", onDisconnect);
  }, [supported]);

  const connect = useCallback(async () => {
    const d = await requestTecorDevice();
    setConnected(true);
    setDeviceName(d.productName || "Tecor TR-422B");
    return d;
  }, []);

  const printB64 = useCallback(async (b64) => {
    setBusy(true);
    try {
      return await printBytes(b64ToBytes(b64));
    } finally {
      setBusy(false);
    }
  }, []);

  return { supported, connected, deviceName, busy, connect, printB64 };
}
